'use client'

// React Imports
import { useState, useCallback, useRef, useEffect } from 'react'

// Next Imports

// MUI Imports
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Box from '@mui/material/Box'
import Fade from '@mui/material/Fade'
import { useTheme } from '@mui/material/styles'

// Component Imports
import IconifyIcon from '@/@core/components/icon'
import CompactView from './weather-widget/components/CompactView'
import ExpandedOverlay from './weather-widget/components/ExpandedOverlay'
import LoadingState from './weather-widget/components/LoadingState'
import ErrorState from './weather-widget/components/ErrorState'
import NoLocationState from './weather-widget/components/NoLocationState'

// Hook Imports
import { useWeatherSimple } from '@/hooks/useWeatherSimple'
import { useGeolocation } from '@/hooks/useGeolocation'

// Utils Imports
import weatherUtils from '@/utils/weatherUtils'

// Types
import type { WeatherWidgetProps } from '@/types/weather/weatherTypes'

const WeatherWidget = ({ loading: externalLoading = false }: WeatherWidgetProps = {}) => {
  const theme = useTheme()
  const cardRef = useRef<HTMLDivElement>(null)

  // Obter idioma atual da URL (igual ao LanguageDropdown)
  const currentLang = 'pt' // Temporariamente fixo enquanto corrigimos params
  const apiLanguage = currentLang

  const { data, loading, error, hasValidLocation, refetch } = useWeatherSimple(apiLanguage)
  const { requestLocation, location } = useGeolocation()

  // Estados locais
  const [isExpanded, setIsExpanded] = useState(false)
  const [isInitializing, setIsInitializing] = useState(true)

  const [originalPosition, setOriginalPosition] = useState<{
    top: number;
    left: number;
    right: number;
    width: number;
    height: number
  } | null>(null)

  // Effect para controlar inicialização e evitar "piscar"
  useEffect(() => {
    // Aguarda um pouco para o Redux carregar o estado da localização
    const timer = setTimeout(() => {
      setIsInitializing(false)
    }, 100) // 100ms é suficiente para o Redux carregar

    return () => clearTimeout(timer)
  }, [])

  // Effect para resetar inicialização quando localização mudar
  useEffect(() => {
    if (location !== null) {
      setIsInitializing(false)
    }
  }, [location])

  // Determinar estado de carregamento
  const isLoading = loading || externalLoading || isInitializing

  // Handler para calcular posição original e expandir
  const handleToggleExpansion = useCallback(() => {
    if (!isExpanded && cardRef.current) {
      const rect = cardRef.current.getBoundingClientRect()

      setOriginalPosition({
        top: rect.top + window.scrollY,
        left: rect.left + window.scrollX,
        right: rect.right + window.scrollX,
        width: rect.width,
        height: rect.height
      })
    }

    setIsExpanded(prev => !prev)
  }, [isExpanded])

  // Handler para fechar expansão
  const handleCloseExpansion = useCallback(() => {
    setIsExpanded(false)
    setOriginalPosition(null)
  }, [])

  // Handler para solicitar localização
  const handleRequestLocation = useCallback(async () => {
    try {
      await requestLocation()
    } catch (error) {
      console.warn('Erro ao solicitar localização:', error)
    }
  }, [requestLocation])

  // Função para obter background glassmorphism
  const getGlassmorphismStyle = useCallback(() => {
    const isDark = theme.palette.mode === 'dark'

    return {
      background: isDark ? 'rgba(10, 10, 10, 0.1)' : 'rgba(255, 255, 255, 0.05)',
      backdropFilter: 'blur(25px) saturate(180%)',
      WebkitBackdropFilter: 'blur(25px) saturate(180%)',
      border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(255, 255, 255, 0.1)'}`,
      borderRadius: 4,
      boxShadow: isDark
        ? '0 4px 20px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.02)'
        : '0 4px 20px rgba(31, 38, 135, 0.05), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
    }
  }, [theme.palette.mode])

  // Função para obter cores baseadas no horário
  const getTimeBasedColors = useCallback((timeString?: string, defaultColor?: string, defaultGradient?: string[]) => {
    if (!timeString) return { color: defaultColor, gradient: defaultGradient }

    const hour = new Date(timeString).getHours()

    // Após 18h (6 PM) ou antes das 6h usar cores azuis para representar período noturno
    if (hour >= 18 || hour < 6) {
      return {
        color: '#64B5F6',
        gradient: ['#42A5F5', '#1E88E5']
      }
    }

    return { color: defaultColor, gradient: defaultGradient }
  }, [])

  // Renderizar ícone do clima
  const renderWeatherIcon = useCallback((conditionCode: number, size: number = 48, isDay?: boolean, timeString?: string) => {
    const iconData = weatherUtils.getWeatherIcon(conditionCode, isDay)
    const timeColors = getTimeBasedColors(timeString, iconData.color, iconData.gradient)
    const gradient = timeColors.gradient || [timeColors.color || iconData.color, timeColors.color || iconData.color]

    return (
      <Box
        sx={{
          width: size,
          height: size,
          borderRadius: 2,
          background: `linear-gradient(135deg, ${gradient[0]}, ${gradient[1]})`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          flexShrink: 0
        }}
      >
        <IconifyIcon icon={iconData.icon} fontSize={`${size * 0.6}px`} color='white' />
      </Box>
    )
  }, [getTimeBasedColors])

  // Estados de carregamento ou erro - renderizados usando subcomponentes
  if (isLoading) {
    return <LoadingState glassmorphismStyle={getGlassmorphismStyle()} />
  }

  // Só mostra NoLocationState se realmente não tiver localização E não estiver inicializando
  if (!hasValidLocation && !isInitializing) {
    return <NoLocationState glassmorphismStyle={getGlassmorphismStyle()} onRequestLocation={handleRequestLocation} />
  }

  if (error && !data) {
    return <ErrorState glassmorphismStyle={getGlassmorphismStyle()} error={error} onRetry={refetch} />
  }

  // Renderização principal - não há dados válidos, retorna null
    if (!data) return null

    return (
    <>
      {/* Card principal compacto */}
    <Fade in timeout={500}>
      <Card
          ref={cardRef}
        elevation={0}
        onClick={handleToggleExpansion}
        sx={{
          ...getGlassmorphismStyle(),
          cursor: 'pointer',
          transition: 'all 0.3s ease',
            width: { xs: '100%', sm: 'fit-content' }, // Largura total em mobile, fit-content em desktop
          maxWidth: '100%',
          height: { xs: '100%'},
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow:
              theme.palette.mode === 'dark'
                ? '0 12px 40px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
                : '0 12px 40px rgba(31, 38, 135, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.6)'
          }
        }}
      >
                  <CardContent
            sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              width: { xs: '100%', sm: 'fit-content' }, // Largura total em mobile
              minWidth: 'fit-content',
              '&:last-child': { pb: 2 }
            }}
          >
            <CompactView
              data={data}
              isExpanded={isExpanded}
              renderWeatherIcon={renderWeatherIcon}
            />
        </CardContent>
      </Card>
    </Fade>

      {/* Overlay expandido */}
      <ExpandedOverlay
        data={data}
        isExpanded={isExpanded}
        onClose={handleCloseExpansion}
        glassmorphismStyle={getGlassmorphismStyle()}
        renderWeatherIcon={renderWeatherIcon}
        originalPosition={originalPosition}
      />
    </>
  )
}

export default WeatherWidget
